import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { usePipeline } from "@/hooks/use-pipeline";
import { useWorkspace } from "@/hooks/use-workspace";
import { MockProperty } from "@/data/mockProperties";
import {
  DEFAULT_FAMILY_SIZE,
  classifyIncome,
  type EligibilityResult,
  type IncomeLimits,
  type Section8PropertyOption,
} from "@/components/section8/wizard/data";

/**
 * Powers the Section 8 case wizard's eligibility step.
 *
 *  - `properties`: units the workspace lists for rent AND accepts vouchers on.
 *  - `checkEligibility`: zip → HUD area (safmr_rates) → annual income limits
 *    (section8_income_limits), then classifies the tenant's income.
 *
 * `safmr_rates` and `section8_income_limits` are static public reference tables
 * not present in the generated Supabase types, so they are read through a
 * narrowly-typed view of the client (same pattern as use-pipeline).
 */

interface SafmrRow {
  hud_area_code: string;
  hud_area_name: string;
}

type RefRow = Record<string, number | string | null>;

interface RefFilter<T> extends PromiseLike<{ data: T[] | null; error: unknown }> {
  eq: (col: string, val: unknown) => RefFilter<T>;
  limit: (n: number) => RefFilter<T>;
  maybeSingle: () => Promise<{ data: T | null; error: unknown }>;
}

interface RefClient {
  from: (table: string) => { select: <T>(cols: string) => RefFilter<T> };
}

const refDb = supabase as unknown as RefClient;

const clampFamilySize = (n: number) => Math.min(8, Math.max(1, Math.round(n)));

export function useSection8Eligibility() {
  const { currentWorkspace } = useWorkspace();
  const { savedProperties } = usePipeline();
  const workspaceId = currentWorkspace?.id;

  const listingsQuery = useQuery({
    queryKey: ["section8-eligible-listings", workspaceId],
    enabled: !!workspaceId,
    queryFn: async () => {
      const { data, error } = await refDb
        .from("rental_listings")
        .select<{ pipeline_property_id: string }>("pipeline_property_id")
        .eq("workspace_id", workspaceId)
        .eq("accept_section8_voucher", true);
      if (error) throw error;
      return data ?? [];
    },
  });

  const properties: Section8PropertyOption[] = useMemo(() => {
    const byId = new Map(savedProperties.map((sp) => [sp.id, sp]));
    const seen = new Set<string>();
    const options: Section8PropertyOption[] = [];
    for (const listing of listingsQuery.data ?? []) {
      const id = listing.pipeline_property_id;
      if (!id || seen.has(id)) continue;
      const pd = byId.get(id)?.property_data as unknown as MockProperty | undefined;
      if (!pd) continue;
      seen.add(id);
      options.push({
        value: id,
        label: pd.address || "Property",
        zip: pd.zip ?? "",
        city: pd.city ?? "",
        state: pd.state ?? "",
        lat: typeof pd.lat === "number" ? pd.lat : undefined,
        lng: typeof pd.lng === "number" ? pd.lng : undefined,
      });
    }
    return options;
  }, [listingsQuery.data, savedProperties]);

  const checkEligibility = useCallback(
    async ({ zip, monthlySalary, familySize = DEFAULT_FAMILY_SIZE }: {
      zip: string;
      monthlySalary: string;
      familySize?: number;
    }): Promise<EligibilityResult> => {
      const size = clampFamilySize(familySize);
      const annualIncome = Math.round((parseFloat(monthlySalary) || 0) * 12);
      const base: EligibilityResult = {
        status: "fail", bracket: null, hudAreaName: null, annualIncome, limits: null, error: null,
      };
      if (!zip || annualIncome <= 0) return { ...base, error: "missing_input" };

      const { data: safmr, error: safmrErr } = await refDb
        .from("safmr_rates")
        .select<SafmrRow>("hud_area_code, hud_area_name")
        .eq("zip_code", zip)
        .limit(1)
        .maybeSingle();
      if (safmrErr) throw safmrErr;
      if (!safmr) return { ...base, error: "no_zip_match" };

      const cols = `extremely_low_income_${size}p, very_low_income_${size}p, low_income_${size}p, hud_area_name`;
      const { data: row, error: limitErr } = await refDb
        .from("section8_income_limits")
        .select<RefRow>(cols)
        .eq("hud_area_code", safmr.hud_area_code)
        .limit(1)
        .maybeSingle();
      if (limitErr) throw limitErr;

      const hudAreaName = (row?.hud_area_name as string) || safmr.hud_area_name;
      if (!row) return { ...base, hudAreaName, error: "no_income_data" };

      const limits: IncomeLimits = {
        extremelyLow: Number(row[`extremely_low_income_${size}p`]),
        veryLow: Number(row[`very_low_income_${size}p`]),
        low: Number(row[`low_income_${size}p`]),
      };
      const bracket = classifyIncome(annualIncome, limits);
      if (!bracket) return { status: "fail", bracket: null, hudAreaName, annualIncome, limits, error: "over_limit" };
      return { status: "pass", bracket, hudAreaName, annualIncome, limits, error: null };
    },
    [],
  );

  return {
    properties,
    isLoadingProperties: listingsQuery.isLoading,
    checkEligibility,
  };
}
